import type { Country } from './types';

/**
 * Asian countries with full metadata.
 */
export const asiaCountries: Country[] = [
  {
    code: 'JP',
    name: 'Japan',
    flag: '\u{1F1EF}\u{1F1F5}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Tokyo',
      population: '125.7M',
      currency: 'JPY (\u00A5)',
      languages: 'Japanese',
      area: '377,975 km\u00B2',
    },
    mapHints: {
      // Japan is an archipelago - show all main islands
      fitStrategy: 'full',
    },
  },
  {
    code: 'KR',
    name: 'South Korea',
    flag: '\u{1F1F0}\u{1F1F7}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Seoul',
      population: '51.7M',
      currency: 'KRW (\u20A9)',
      languages: 'Korean',
      area: '100,210 km\u00B2',
    },
  },
  {
    code: 'CN',
    name: 'China',
    flag: '\u{1F1E8}\u{1F1F3}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Beijing',
      population: '1.41B',
      currency: 'CNY (\u00A5)',
      languages: 'Mandarin',
      area: '9,596,961 km\u00B2',
    },
  },
  {
    code: 'IN',
    name: 'India',
    flag: '\u{1F1EE}\u{1F1F3}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'New Delhi',
      population: '1.42B',
      currency: 'INR (\u20B9)',
      languages: 'Hindi, English',
      area: '3,287,263 km\u00B2',
    },
    mapHints: {
      // India has Andaman & Nicobar Islands (far away) and Lakshadweep
      // Show mainland + close islands, exclude very distant ones
      fitStrategy: 'auto',
      maxTerritoryDistance: 1500, // Include Andaman Islands (~1200km)
      minAreaRatio: 0.01,
    },
  },
  {
    code: 'TH',
    name: 'Thailand',
    flag: '\u{1F1F9}\u{1F1ED}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Bangkok',
      population: '70.0M',
      currency: 'THB (\u0E3F)',
      languages: 'Thai',
      area: '513,120 km\u00B2',
    },
  },
  {
    code: 'SG',
    name: 'Singapore',
    flag: '\u{1F1F8}\u{1F1EC}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Singapore',
      population: '5.9M',
      currency: 'SGD (S$)',
      languages: 'English, Mandarin, Malay, Tamil',
      area: '733 km\u00B2',
    },
  },
  {
    code: 'MY',
    name: 'Malaysia',
    flag: '\u{1F1F2}\u{1F1FE}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Kuala Lumpur',
      population: '33.0M',
      currency: 'MYR (RM)',
      languages: 'Malay, English, Mandarin, Tamil',
      area: '330,803 km\u00B2',
    },
    mapHints: {
      // Malaysia has two main parts: Peninsular Malaysia and East Malaysia (Borneo)
      // Both are essential parts of the country (~600km apart)
      // Must show BOTH regions
      fitStrategy: 'full',
    },
  },
  {
    code: 'ID',
    name: 'Indonesia',
    flag: '\u{1F1EE}\u{1F1E9}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Jakarta',
      population: '277.5M',
      currency: 'IDR (Rp)',
      languages: 'Indonesian',
      area: '1,904,569 km\u00B2',
    },
    mapHints: {
      // Indonesia is a massive archipelago - show all major islands
      fitStrategy: 'full',
    },
  },
  {
    code: 'VN',
    name: 'Vietnam',
    flag: '\u{1F1FB}\u{1F1F3}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Hanoi',
      population: '99.5M',
      currency: 'VND (\u20AB)',
      languages: 'Vietnamese',
      area: '331,212 km\u00B2',
    },
  },
  {
    code: 'PH',
    name: 'Philippines',
    flag: '\u{1F1F5}\u{1F1ED}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Manila',
      population: '113.9M',
      currency: 'PHP (\u20B1)',
      languages: 'Filipino, English',
      area: '300,000 km\u00B2',
    },
    mapHints: {
      // Philippines is an archipelago - show all main islands
      fitStrategy: 'full',
    },
  },
  {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '\u{1F1E6}\u{1F1EA}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Abu Dhabi',
      population: '10.0M',
      currency: 'AED (\u062F.\u0625)',
      languages: 'Arabic, English',
      area: '83,600 km\u00B2',
    },
  },
  {
    code: 'IL',
    name: 'Israel',
    flag: '\u{1F1EE}\u{1F1F1}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Jerusalem',
      population: '9.5M',
      currency: 'ILS (\u20AA)',
      languages: 'Hebrew, Arabic',
      area: '22,072 km\u00B2',
    },
  },
  {
    code: 'TR',
    name: 'Turkey',
    officialName: 'T\u00FCrkiye',
    flag: '\u{1F1F9}\u{1F1F7}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Ankara',
      population: '85.3M',
      currency: 'TRY (\u20BA)',
      languages: 'Turkish',
      area: '783,356 km\u00B2',
    },
  },
  {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '\u{1F1F8}\u{1F1E6}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Riyadh',
      population: '35.0M',
      currency: 'SAR (\uFDFC)',
      languages: 'Arabic',
      area: '2,149,690 km\u00B2',
    },
  },
  {
    code: 'QA',
    name: 'Qatar',
    flag: '\u{1F1F6}\u{1F1E6}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Doha',
      population: '2.9M',
      currency: 'QAR (\uFDFC)',
      languages: 'Arabic',
      area: '11,586 km\u00B2',
    },
  },
  {
    code: 'KW',
    name: 'Kuwait',
    flag: '\u{1F1F0}\u{1F1FC}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Kuwait City',
      population: '4.3M',
      currency: 'KWD (\u062F.\u0643)',
      languages: 'Arabic',
      area: '17,818 km\u00B2',
    },
  },
  {
    code: 'OM',
    name: 'Oman',
    flag: '\u{1F1F4}\u{1F1F2}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Muscat',
      population: '5.2M',
      currency: 'OMR (\u0631.\u0639.)',
      languages: 'Arabic',
      area: '309,500 km\u00B2',
    },
  },
  {
    code: 'BH',
    name: 'Bahrain',
    flag: '\u{1F1E7}\u{1F1ED}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Manama',
      population: '1.5M',
      currency: 'BHD (\u062F.\u0628)',
      languages: 'Arabic',
      area: '786 km\u00B2',
    },
  },
  {
    code: 'JO',
    name: 'Jordan',
    flag: '\u{1F1EF}\u{1F1F4}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Amman',
      population: '10.3M',
      currency: 'JOD (\u062F.\u0623)',
      languages: 'Arabic',
      area: '89,342 km\u00B2',
    },
  },
  {
    code: 'LB',
    name: 'Lebanon',
    flag: '\u{1F1F1}\u{1F1E7}',
    continent: 'Asia',
    subregion: 'Middle East',
    details: {
      capital: 'Beirut',
      population: '5.5M',
      currency: 'LBP (\u0644.\u0644)',
      languages: 'Arabic',
      area: '10,452 km\u00B2',
    },
  },
  {
    code: 'TW',
    name: 'Taiwan',
    flag: '\u{1F1F9}\u{1F1FC}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Taipei',
      population: '23.6M',
      currency: 'TWD (NT$)',
      languages: 'Mandarin',
      area: '36,193 km\u00B2',
    },
  },
  {
    code: 'HK',
    name: 'Hong Kong',
    flag: '\u{1F1ED}\u{1F1F0}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Hong Kong',
      population: '7.4M',
      currency: 'HKD (HK$)',
      languages: 'Cantonese, English',
      area: '1,114 km\u00B2',
    },
  },
  {
    code: 'KH',
    name: 'Cambodia',
    flag: '\u{1F1F0}\u{1F1ED}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Phnom Penh',
      population: '16.7M',
      currency: 'KHR (\u17DB)',
      languages: 'Khmer',
      area: '181,035 km\u00B2',
    },
  },
  {
    code: 'MM',
    name: 'Myanmar',
    flag: '\u{1F1F2}\u{1F1F2}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Naypyidaw',
      population: '54.4M',
      currency: 'MMK (K)',
      languages: 'Burmese',
      area: '676,578 km\u00B2',
    },
  },
  {
    code: 'LA',
    name: 'Laos',
    flag: '\u{1F1F1}\u{1F1E6}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Vientiane',
      population: '7.4M',
      currency: 'LAK (\u20AD)',
      languages: 'Lao',
      area: '236,800 km\u00B2',
    },
  },
  {
    code: 'NP',
    name: 'Nepal',
    flag: '\u{1F1F3}\u{1F1F5}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Kathmandu',
      population: '30.0M',
      currency: 'NPR (\u0930\u0942)',
      languages: 'Nepali',
      area: '147,516 km\u00B2',
    },
  },
  {
    code: 'LK',
    name: 'Sri Lanka',
    flag: '\u{1F1F1}\u{1F1F0}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Colombo',
      population: '22.0M',
      currency: 'LKR (Rs)',
      languages: 'Sinhala, Tamil',
      area: '65,610 km\u00B2',
    },
  },
  {
    code: 'BD',
    name: 'Bangladesh',
    flag: '\u{1F1E7}\u{1F1E9}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Dhaka',
      population: '169.4M',
      currency: 'BDT (\u09F3)',
      languages: 'Bengali',
      area: '147,570 km\u00B2',
    },
  },
  {
    code: 'PK',
    name: 'Pakistan',
    flag: '\u{1F1F5}\u{1F1F0}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Islamabad',
      population: '231.4M',
      currency: 'PKR (\u20A8)',
      languages: 'Urdu, English',
      area: '881,913 km\u00B2',
    },
  },
  {
    code: 'MN',
    name: 'Mongolia',
    flag: '\u{1F1F2}\u{1F1F3}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Ulaanbaatar',
      population: '3.4M',
      currency: 'MNT (\u20AE)',
      languages: 'Mongolian',
      area: '1,564,116 km\u00B2',
    },
  },
  {
    code: 'KZ',
    name: 'Kazakhstan',
    flag: '\u{1F1F0}\u{1F1FF}',
    continent: 'Asia',
    subregion: 'Central Asia',
    details: {
      capital: 'Astana',
      population: '19.4M',
      currency: 'KZT (\u20B8)',
      languages: 'Kazakh, Russian',
      area: '2,724,900 km\u00B2',
    },
  },
  {
    code: 'UZ',
    name: 'Uzbekistan',
    flag: '\u{1F1FA}\u{1F1FF}',
    continent: 'Asia',
    subregion: 'Central Asia',
    details: {
      capital: 'Tashkent',
      population: '34.9M',
      currency: 'UZS (so\u02BBm)',
      languages: 'Uzbek',
      area: '448,978 km\u00B2',
    },
  },
  {
    code: 'GE',
    name: 'Georgia',
    flag: '\u{1F1EC}\u{1F1EA}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Tbilisi',
      population: '3.7M',
      currency: 'GEL (\u20BE)',
      languages: 'Georgian',
      area: '69,700 km\u00B2',
    },
  },
  {
    code: 'AM',
    name: 'Armenia',
    flag: '\u{1F1E6}\u{1F1F2}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Yerevan',
      population: '3.0M',
      currency: 'AMD (\u058F)',
      languages: 'Armenian',
      area: '29,743 km\u00B2',
    },
  },
  {
    code: 'AZ',
    name: 'Azerbaijan',
    flag: '\u{1F1E6}\u{1F1FF}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Baku',
      population: '10.2M',
      currency: 'AZN (\u20BC)',
      languages: 'Azerbaijani',
      area: '86,600 km\u00B2',
    },
  },
  {
    code: 'MV',
    name: 'Maldives',
    flag: '\u{1F1F2}\u{1F1FB}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Mal\u00E9',
      population: '521K',
      currency: 'MVR (Rf)',
      languages: 'Dhivehi',
      area: '300 km\u00B2',
    },
  },
  {
    code: 'BN',
    name: 'Brunei',
    flag: '\u{1F1E7}\u{1F1F3}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Bandar Seri Begawan',
      population: '441K',
      currency: 'BND (B$)',
      languages: 'Malay',
      area: '5,765 km\u00B2',
    },
  },
  {
    code: 'PS',
    name: 'Palestine',
    flag: '\u{1F1F5}\u{1F1F8}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Ramallah',
      population: '5.4M',
      currency: 'ILS (\u20AA)',
      languages: 'Arabic',
      area: '6,020 km\u00B2',
    },
  },
  {
    code: 'AF',
    name: 'Afghanistan',
    flag: '\u{1F1E6}\u{1F1EB}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Kabul',
      population: '43.8M',
      currency: 'AFN (\u060B)',
      languages: 'Dari, Pashto',
      area: '652,230 km\u00B2',
    },
  },
  {
    code: 'BT',
    name: 'Bhutan',
    flag: '\u{1F1E7}\u{1F1F9}',
    continent: 'Asia',
    subregion: 'South Asia',
    details: {
      capital: 'Thimphu',
      population: '784K',
      currency: 'BTN (Nu.)',
      languages: 'Dzongkha',
      area: '38,394 km\u00B2',
    },
  },
  {
    code: 'IR',
    name: 'Iran',
    flag: '\u{1F1EE}\u{1F1F7}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Tehran',
      population: '86M',
      currency: 'IRR (\uFDFC)',
      languages: 'Persian (Farsi)',
      area: '1,648,195 km\u00B2',
    },
  },
  {
    code: 'IQ',
    name: 'Iraq',
    flag: '\u{1F1EE}\u{1F1F6}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Baghdad',
      population: '46.1M',
      currency: 'IQD (\u0639.\u062F)',
      languages: 'Arabic, Kurdish',
      area: '438,317 km\u00B2',
    },
  },
  {
    code: 'KG',
    name: 'Kyrgyzstan',
    flag: '\u{1F1F0}\u{1F1EC}',
    continent: 'Asia',
    subregion: 'Central Asia',
    details: {
      capital: 'Bishkek',
      population: '7.3M',
      currency: 'KGS (\u0441)',
      languages: 'Kyrgyz, Russian',
      area: '199,951 km\u00B2',
    },
  },
  {
    code: 'KP',
    name: 'North Korea',
    flag: '\u{1F1F0}\u{1F1F5}',
    continent: 'Asia',
    subregion: 'East Asia',
    details: {
      capital: 'Pyongyang',
      population: '25.9M',
      currency: 'KPW (\u20A9)',
      languages: 'Korean',
      area: '120,538 km\u00B2',
    },
  },
  {
    code: 'SY',
    name: 'Syria',
    flag: '\u{1F1F8}\u{1F1FE}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: 'Damascus',
      population: '25.6M',
      currency: 'SYP (\u00A3)',
      languages: 'Arabic',
      area: '185,180 km\u00B2',
    },
  },
  {
    code: 'TJ',
    name: 'Tajikistan',
    flag: '\u{1F1F9}\u{1F1EF}',
    continent: 'Asia',
    subregion: 'Central Asia',
    details: {
      capital: 'Dushanbe',
      population: '10.5M',
      currency: 'TJS (\u0405\u041C)',
      languages: 'Russian, Tajik',
      area: '143,100 km\u00B2',
    },
  },
  {
    code: 'TL',
    name: 'Timor-Leste',
    officialName: 'Democratic Republic of Timor-Leste',
    flag: '\u{1F1F9}\u{1F1F1}',
    continent: 'Asia',
    subregion: 'Southeast Asia',
    details: {
      capital: 'Dili',
      population: '1.4M',
      currency: 'USD ($)',
      languages: 'Portuguese, Tetum',
      area: '14,874 km\u00B2',
    },
  },
  {
    code: 'TM',
    name: 'Turkmenistan',
    flag: '\u{1F1F9}\u{1F1F2}',
    continent: 'Asia',
    subregion: 'Central Asia',
    details: {
      capital: 'Ashgabat',
      population: '7.1M',
      currency: 'TMT (m)',
      languages: 'Russian, Turkmen',
      area: '488,100 km\u00B2',
    },
  },
  {
    code: 'YE',
    name: 'Yemen',
    flag: '\u{1F1FE}\u{1F1EA}',
    continent: 'Asia',
    subregion: 'Western Asia',
    details: {
      capital: "Sana'a",
      population: '32.7M',
      currency: 'YER (\uFDFC)',
      languages: 'Arabic',
      area: '527,968 km\u00B2',
    },
  },
];
